import Config from "../../components/common/Config";
import { resolveCartChannelId } from "../cart";
import { extractMaximumOrderQuantityFields } from "../../components/sales/common/maximumOrderQuantity";
import {
    buildProductSearchIndexUrl,
    normalizeProductSearchApiRoot,
} from "../../utils/productSearchUrls";
import { searchTokensFromQuery, productMatchesSearchTokens, sortProductsBySearchRelevance, buildProductSearchTokens } from "../../productSearch/matchProductTokens";
import { postEncryptedForm, postEncryptedJson, joinApiUrl } from "../../utils/eposApi";

const productSearchIndexSessionCache = new Map();

const getSearchIndexCacheKey = (baseUrl) =>
    `${String(Config.PREFIX || "default")}::${String(baseUrl || "").toLowerCase()}`;

const loadSearchIndexOnce = async ({ baseUrl, authToken, forceRefresh = false }) => {
    const cacheKey = getSearchIndexCacheKey(baseUrl);
    if (!forceRefresh) {
        const cached = productSearchIndexSessionCache.get(cacheKey);
        if (cached && Array.isArray(cached)) {
            return cached;
        }
    }
    const url = buildProductSearchIndexUrl(baseUrl);
    const response = await fetch(url, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${authToken}`,
        },
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const payload = await response.json();
    const rawProducts = Array.isArray(payload?.data) ? payload.data : [];
    productSearchIndexSessionCache.set(cacheKey, rawProducts);
    return rawProducts;
};

export const normalizeSearchIndexProduct = (p) => {
    const code = p?.product_code ?? p?.code ?? "";
    const name = p?.product_name ?? p?.name ?? "";
    const row = {
        ...p,
        id: p?.id ?? p?.product_id ?? "",
        code: String(code),
        name: String(name),
        product_code: String(code),
        product_name: String(name),
        ...extractMaximumOrderQuantityFields(p),
    };
    row.searchTokens = buildProductSearchTokens(row);
    return row;
};

/** Lookup catalog rows by product id (uses cached search index). */
export const getCatalogProductsByIds = async (productIds, options = {}) => {
    const idSet = new Set(
        (Array.isArray(productIds) ? productIds : [])
            .map((id) => String(id ?? "").trim())
            .filter(Boolean)
    );
    if (!idSet.size) {
        return [];
    }
    const jwtToken = localStorage.getItem("token");
    const accessToken = localStorage.getItem("accessToken");
    const authToken = jwtToken || accessToken;
    const base = options.apiBaseUrl
        ? normalizeProductSearchApiRoot(options.apiBaseUrl)
        : normalizeProductSearchApiRoot(Config.API_URL);
    const rawProducts = await loadSearchIndexOnce({
        baseUrl: base,
        authToken,
        forceRefresh: options.forceRefresh === true,
    });
    return rawProducts
        .filter((p) => idSet.has(String(p?.product_id ?? p?.id ?? "").trim()))
        .map(normalizeSearchIndexProduct);
};

export const getCustomerList = async (data) => {
    const accessToken = localStorage.getItem("accessToken") || Config.PREFIX || "rhp";
    const formData = new FormData();
    formData.append("platform", "web");
    formData.append("pattern", data.pattern || data.customer_name || "");
    formData.append("customer_name", data.customer_name || data.pattern || "");
    formData.append("account_no", data.account_no || data.account_number || "");
    formData.append("account_number", data.account_number || data.account_no || "");
    formData.append("postcode", data.postcode || data.postal_code || "");
    formData.append("postal_code", data.postal_code || data.postcode || "");
    formData.append("authorization", `Bearer ${accessToken}`);
    const response = await fetch(`${Config.API_URL}/ws-get-customer-list-pos`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseData = await response.json();
    return responseData;
}
//TELE_CALLING
export const getProductsList = async (data) => {
    const jwtToken = localStorage.getItem("token");
    const accessToken = localStorage.getItem("accessToken");
    const authToken = jwtToken || accessToken;
    if (data?.useSearchIndex) {
        const base = data.apiBaseUrl
            ? normalizeProductSearchApiRoot(data.apiBaseUrl)
            : normalizeProductSearchApiRoot(Config.API_URL);
        const rawProducts = await loadSearchIndexOnce({ baseUrl: base, authToken, forceRefresh: data?.forceRefresh });
        const normalizedProducts = rawProducts.map((p) => normalizeSearchIndexProduct(p));
        const query = String(data?.pattern || "").trim().toLowerCase();
        const tokens = searchTokensFromQuery(query);
        const filtered = data?.returnAllIndex
            ? normalizedProducts
            : tokens.length === 0
                ? []
                : sortProductsBySearchRelevance(
                      normalizedProducts.filter((p) => productMatchesSearchTokens(p, tokens)),
                      tokens
                  );
        const product_details = filtered.map((p) => ({
            id: p?.id,
            product_id: p?.product_id ?? p?.id,
            product_code: p?.product_code ?? "",
            product_name: p?.product_name ?? "",
            split: String(p?.split ?? "2"),
            price: p?.price ?? p?.final_price ?? 0,
            split_price: p?.split_price ?? p?.final_split_price ?? 0,
            final_price: p?.final_price ?? p?.price ?? 0,
            final_split_price: p?.final_split_price ?? p?.split_price ?? 0,
            stock: p?.stock ?? 0,
            rate: p?.rate ?? p?.tax_rate ?? 0,
            tax_rate: p?.tax_rate ?? p?.rate ?? 0,
            tax_code: p?.tax_code ?? p?.taxCode ?? p?.vat_code ?? "",
            noOfPieceInBox: p?.noOfPieceInBox ?? p?.pack_size ?? "N/A",
            availableBoxQty: p?.availableBoxQty ?? p?.available_box_qty ?? 0,
            availablePieceQty: p?.availablePieceQty ?? p?.available_piece_qty ?? 0,
            ...extractMaximumOrderQuantityFields(p),
        }));
        return { product_details };
    }

    const plainPayload = {
        pattern: data.pattern,
        customer_id: data.customer_id,
    };
    if (data.price_brand !== undefined && data.price_brand !== null && data.price_brand !== "") {
        plainPayload.price_brand = String(data.price_brand);
    }
    if (data.orderChannel) {
        plainPayload.orderChannel = data.orderChannel;
        plainPayload.platform = data.platform || data.orderChannel;
    } else if (data.platform) {
        plainPayload.platform = data.platform;
    } else {
        plainPayload.platform = "5";
    }

    const productsListBase = data.apiBaseUrl
        ? normalizeProductSearchApiRoot(data.apiBaseUrl)
        : (Config.PREFIX === "rhp"
            ? normalizeProductSearchApiRoot(Config.API_URL)
            : Config.API_URL);

    const { response, data: responseData } = await postEncryptedForm(
        `${productsListBase}/${Config.PRODUCTS_LIST_PATH}`,
        authToken,
        plainPayload
    );
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (Array.isArray(responseData?.product_details)) {
        responseData.product_details = responseData.product_details.map((p) => ({
            ...p,
            ...extractMaximumOrderQuantityFields(p),
        }));
    }
    return responseData;
}

export const fetchProductForPriceBrand = async ({
    pattern,
    customerId,
    priceBrand,
    platform = "4",
} = {}) => {
    const searchPattern = String(pattern || "").trim();
    if (!searchPattern || !customerId) {
        return null;
    }
    const response = await getProductsList({
        pattern: searchPattern,
        customer_id: customerId,
        platform,
        price_brand: priceBrand,
    });
    const list =
        response?.product_details ||
        response?.product_detials ||
        [];
    return list[0] || null;
};

const pickStockFieldsFromProduct = (p) => ({
    availableBoxQty: p?.availableBoxQty ?? p?.available_box_qty ?? p?.stock ?? 0,
    availablePieceQty: p?.availablePieceQty ?? p?.available_piece_qty ?? 0,
    stock: p?.stock ?? p?.stock_qty ?? 0,
});

export const fetchProductStockMapForCartLines = async ({
    cartItems = [],
    customerId,
    platform = "5",
    useSearchIndex = false,
    apiBaseUrl,
} = {}) => {
    const map = new Map();
    const ids = new Set();
    const codeById = new Map();
    (cartItems || []).forEach((item) => {
        const pid = String(item?.product_id ?? item?.productId ?? "").trim();
        if (!pid) return;
        ids.add(pid);
        if (!codeById.has(pid)) {
            codeById.set(pid, String(item?.product_code ?? item?.code ?? "").trim());
        }
    });
    if (ids.size === 0) return map;

    if (useSearchIndex) {
        const jwtToken = localStorage.getItem("token");
        const accessToken = localStorage.getItem("accessToken");
        const authToken = jwtToken || accessToken;
        const base = apiBaseUrl
            ? normalizeProductSearchApiRoot(apiBaseUrl)
            : normalizeProductSearchApiRoot(Config.API_URL);
        const rawProducts = await loadSearchIndexOnce({ baseUrl: base, authToken });
        rawProducts.forEach((p) => {
            const id = String(p?.id ?? p?.product_id ?? "").trim();
            if (!ids.has(id)) return;
            map.set(id, pickStockFieldsFromProduct(p));
        });
    } else {
        await Promise.all(
            [...ids].map(async (pid) => {
                const pattern = codeById.get(pid) || pid;
                try {
                    const resp = await getProductsList({
                        pattern,
                        customer_id: customerId,
                        platform,
                    });
                    const list = resp?.product_details || resp?.product_detials || [];
                    const match =
                        list.find(
                            (p) => String(p?.id ?? p?.product_id ?? "").trim() === pid
                        ) || list[0];
                    if (match) {
                        map.set(pid, pickStockFieldsFromProduct(match));
                    }
                } catch {}
            })
        );
    }

    [...ids].forEach((pid) => {
        if (!map.has(pid)) {
            map.set(pid, { availableBoxQty: 0, availablePieceQty: 0, stock: 0 });
        }
    });

    return map;
};

export const fetchProductSearchIndex = async (options = {}) => {
    const jwtToken = localStorage.getItem("token");
    const accessToken = localStorage.getItem("accessToken");
    const url = buildProductSearchIndexUrl(Config.API_URL);
    const fetchOpts = {
        method: "GET",
        headers: {
            Authorization: `Bearer ${jwtToken || accessToken}`,
        },
    };
    if (options.signal) {
        fetchOpts.signal = options.signal;
    }
    const response = await fetch(url, fetchOpts);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
};

export const getIncompleteOrder = async () => {
    const accessToken = localStorage.getItem("accessToken");
    const response = await fetch(`${Config.API_URL}/ws-incomplete-order`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${accessToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseData = await response.json();
    return responseData;
}

export const getCartIncompleteOrders = async ({
    channel_id = 5,
    start = 0,
    length = 50,
    search = "",
  } = {}) => {
    const jwtToken = localStorage.getItem("token");
    const accessToken = localStorage.getItem("accessToken");
    const token = jwtToken || accessToken;
  
    const formData = new FormData();
    formData.append("channel_id", resolveCartChannelId(channel_id));
    formData.append("draw", "1");
    formData.append("start", start);
    formData.append("length", length);
    formData.append("search[value]", search);
    formData.append("search[regex]", "false");
    formData.append("searchValue", search);
  
    const response = await fetch(`${Config.API_URL}/cart_incomplete_orders`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,

      },
      body: formData,
    });
  
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  
    return await response.json();
  };

export const deleteIncompleteOrder = async (id) => {
    const accessToken = localStorage.getItem("accessToken");
    const response = await fetch(`${Config.API_URL}/ws-delete-pos-order/${id}`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${accessToken}`,
        },
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseData = await response.json();
    return responseData;
}

export const placeOrder = async (orderData) => {
    const jwtToken = localStorage.getItem("token");
    const accessToken = localStorage.getItem("accessToken");
    const token = jwtToken || accessToken;
    const response = await fetch(`${Config.API_URL}/ws-place-order-v2_pos`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "text/plain;charset=UTF-8",
        },
        body: JSON.stringify(orderData),
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseData = await response.json();
    return responseData;
}

export const fetchPlaceOrder = async (id) => {
    const accessToken = localStorage.getItem("accessToken");
    const token = localStorage.getItem("token");
    const authToken = token || accessToken;
    const { response, data: responseData } = await postEncryptedForm(
        `${Config.API_URL}/ws-fetch-place-order/${id}`,
        authToken,
        { order_id: String(id) }
    );
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return responseData;
};

export const releasePlaceOrderLock = async (id, options = {}) => {
    const accessToken = localStorage.getItem("accessToken");
    const token = localStorage.getItem("token");
    const auth = token || accessToken;
    if (!id || !auth) return;
    try {
        await fetch(`${Config.API_URL}/ws-close-fetch-place-order/${id}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${auth}`,
                authorization: `Bearer ${auth}`,
            },
            keepalive: Boolean(options.keepalive),
        });
    } catch (_) {}
};

const toTwoDecimalString = (val) => {
    if (val === undefined || val === null || val === "") return val;
    const num = parseFloat(val);
    return Number.isFinite(num) ? num.toFixed(2) : val;
};

export const checkProductPromotion = async (data, options = {}) => {
    const { signal } = options;
    const accessToken = localStorage.getItem("accessToken");
    const token = localStorage.getItem("token");
    const authToken = token || accessToken;
    const rawItems = data.items || [];

    const items = rawItems.map((item) => {
        const hasPromo =
            item.promotion_applied === true ||
            (item.promotion_id && Number(item.promotion_id) > 0) ||
            (item.promosId && Number(item.promosId) > 0);

        const isBuyWithNoPromoDiscount =
            item.promo_type === "BUY" &&
            (parseFloat(item.promo_disc_percentage) || 0) === 0;

        const usePromotionalDiscount = hasPromo && !isBuyWithNoPromoDiscount;

        const promoDiscAmount =
            item.promo_disc_amount !== undefined &&
            item.promo_disc_amount !== null &&
            item.promo_disc_amount !== ""
                ? parseFloat(item.promo_disc_amount)
                : null;

        const discountStr =
            item.item_discount !== undefined &&
            item.item_discount !== null &&
            item.item_discount !== ""
                ? String(item.item_discount).trim()
                : null;

        const discountFromItemDiscount =
            discountStr !== null ? (parseFloat(discountStr) || 0) : 0;

        const promotionalAmount =
            promoDiscAmount !== null ? promoDiscAmount : discountFromItemDiscount;

        const out = { ...item };

        if (usePromotionalDiscount) {
            out.promotional_discount = promotionalAmount;
            delete out.item_discount;
        }

        const origPriceVal = item.original_price !== undefined && item.original_price !== null && item.original_price !== ""
            ? parseFloat(item.original_price)
            : (parseFloat(item.price) || parseFloat(item.unit_price) || 0);
        out.original_price = Number.isFinite(origPriceVal) ? origPriceVal : 0;

        if (item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== "") {
            out.unit_price = toTwoDecimalString(item.unit_price);
        }
        if (item.price !== undefined && item.price !== null && item.price !== "") {
            out.price = toTwoDecimalString(item.price);
        }
        if (item.category_name != null && item.category_name !== "") {
            out.category_name = String(item.category_name).trim();
        }
        out.original_price = toTwoDecimalString(out.original_price);

        delete out.standard_price;
        delete out.promo_disc_amount;
        delete out.promotion_applied;
        delete out.promotion_id;

        return out;
    });

    const payload = {
        order_id: data.order_id ?? null,
        customer_id: data.customer_id,
        channel: data.channel || "pos",
        order_date: data.order_date,
        items
    };

    if (data.order_time) {
        payload.order_time = data.order_time;
    }

    if (data.over_all_discount !== undefined && data.over_all_discount !== null && data.over_all_discount !== "") {
        const val = parseFloat(data.over_all_discount);
        payload.over_all_discount = Number.isFinite(val) ? val : 0;
    }

    if (data.promotion && (Array.isArray(data.promotion.apply) || Array.isArray(data.promotion.remove))) {
        payload.promotion = {
            apply: data.promotion.apply || [],
            remove: data.promotion.remove || []
        };
    }

    if (data.for_edit === true) {
        payload.for_edit = true;
    }

    if (data.preview_only === true) {
        payload.preview_only = true;
    }

    if (Array.isArray(data.changed_get_products) && data.changed_get_products.length > 0) {
        payload.changed_get_products = data.changed_get_products;
    }

    if (data.trigger) {
        payload.trigger = data.trigger;
    }
    if (
        data.changed_product_id !== undefined &&
        data.changed_product_id !== null &&
        data.changed_product_id !== ""
    ) {
        const changedId = parseInt(data.changed_product_id, 10);
        if (Number.isFinite(changedId) && changedId > 0) {
            payload.changed_product_id = changedId;
        }
    }

    const { response, data: responseData } = await postEncryptedJson(
        joinApiUrl(Config.API_URL, 'api/promotions/check-promotion'),
        authToken,
        payload,
        { signal }
    );

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return responseData;
};

export const updateInvoice = async (data) => {
    const accessToken = localStorage.getItem("accessToken");
    const plainPayload = {
        orderId: data.orderId,
        status: data.status,
        group_id: data.group_id,
        delivery_address: data.delivery_address || "",
        over_all_amount: data.over_all_amount || "0.00",
        over_all_discount: data.over_all_discount || "0.00",
        orderList: JSON.stringify(data.orderList),
        staffNote: data.staffNote,
        order_date: data.order_date,
        order_time: data.order_time,
        userId: data.userId,
    };
    if (data.channel) {
        plainPayload.channel = data.channel;
    }

    const { response, data: parsedData, responseText } = await postEncryptedForm(
        `${Config.API_URL}/ws_update_invoice_new`,
        accessToken,
        plainPayload,
        { parse: 'text' }
    );
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    if (parsedData && typeof parsedData === 'object') {
        return parsedData;
    }

    // Get response text first to check if it's valid JSON
    const responseTextValue = responseText ?? '';
    let responseData;
    
    try {
        responseData = JSON.parse(responseTextValue);
    } catch (parseError) {
        if (responseTextValue.includes("Array")) {
            const trimmed = responseTextValue.trim();
            if (trimmed === "Array\n(\n)" || trimmed === "Array()" || trimmed === "Array ( )") {
                throw new Error("Server returned empty response. The order may not have been saved. Please try again or contact support.");
            }

            const looksLikeProcessedLineItems =
                responseTextValue.includes("quantity") &&
                responseTextValue.includes("accept_qty") &&
                responseTextValue.includes("[id]");

            const looksLikeOrderHeaderDump =
                responseTextValue.includes("[customer_id]") &&
                (responseTextValue.includes("[company_id]") ||
                    responseTextValue.includes("[site_id]") ||
                    responseTextValue.includes("[order_date]") ||
                    responseTextValue.includes("[document_status]") ||
                    responseTextValue.includes("EnumDocument"));

            if (looksLikeProcessedLineItems) {
                const itemCount = (responseTextValue.match(/\[id\] =>/g) || []).length;
                if (itemCount > 0) {
                    responseData = {
                        success: true,
                        message: `Invoice updated successfully. ${itemCount} item(s) processed.`,
                    };
                } else {
                    throw new Error("Server processed the request but returned no items. Please check the order and try again.");
                }
            } else if (looksLikeOrderHeaderDump) {
                responseData = {
                    success: true,
                    message: "Invoice updated successfully.",
                };
            } else {
                throw new Error(
                    "The server returned an unexpected format. The update may still have completed. Please open the trip summary again or contact support."
                );
            }
        } else {
            throw new Error("The server returned an unexpected format. Please try again or contact support.");
        }
    }
    
    return responseData;
}

export const updateProductEanNumber = async ({
    productId,
    innerEanNumber,
    outerEanNumber = "",
} = {}) => {
    const jwtToken = localStorage.getItem("token");
    const accessToken = localStorage.getItem("accessToken");
    const authToken = jwtToken || accessToken;
    const { response, data } = await postEncryptedForm(
        `${Config.API_URL}/ws-update-ean-number`,
        authToken,
        {
            product_id: String(productId),
            inner_ean: String(innerEanNumber || ""),
            outer_ean_number: String(outerEanNumber || ""),
        }
    );
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return data;
};

export const updateProductBandPrices = async ({
    productId,
    prices = {},
    offerCc = "",
    expiryCc = "",
} = {}) => {
    const jwtToken = localStorage.getItem("token");
    const accessToken = localStorage.getItem("accessToken");
    const authToken = jwtToken || accessToken;
    const params = new URLSearchParams();
    params.set("product_id", String(productId));
    Object.entries(prices).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
        }
    });
    if (offerCc !== undefined && offerCc !== null && offerCc !== "") {
        params.set("offer_cc", String(offerCc));
    }
    if (expiryCc !== undefined && expiryCc !== null && expiryCc !== "") {
        params.set("expiry_cc", String(expiryCc));
    }
    params.set("platform", "4");

    const response = await fetch(
        `${Config.API_URL}/updatePrice?${params.toString()}`,
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data?.message || `HTTP error! status: ${response.status}`);
    }
    return data;
};